class ContentCarousel extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('.blaze-slider');
    this.blazeInstance = null;
  }

  connectedCallback() {
    this.getConfiguration();
    this.setupSlider();
    this.setupVideoControls();
  }

  getConfiguration() {
    try {
      const config = this.dataset.sliderConfig;
      if (!config) return;
      
      this.sliderConfig = JSON.parse(config);
    } catch (error) {
      console.error('Error parsing slider config', error);
      this.sliderConfig = this.getDefaultConfig();
    }
  }

  getDefaultConfig() {
    return {
      all: {
        slidesToShow: 4,
        slideGap: '1rem',
        loop: false,
        draggable: true,
        transitionDuration: 300,
        transitionTimingFunction: 'cubic-bezier(0.45, 0.1, 0.45, 0.5)',
        slidesToScroll: 1,
        enablePagination: true,
        enableAutoplay: false,
      },
      '(max-width: 1200px)': {
        slidesToShow: 3.5,
      },
      '(max-width: 1024px)': {
        slidesToShow: 3,
      },
      '(max-width: 768px)': {
        slidesToShow: 2,
        slideGap: '0.75rem',
      },
      '(max-width: 400px)': {
        slidesToShow: 1.2,
      },
    };
  }

  setupSlider() {
    if (!window.BlazeSlider) {
      console.error('BlazeSlider not loaded');
      return;
    }

    const sliderElement = this.querySelector('.blaze-slider');
    if (sliderElement) {
      this.applyResponsiveConfig(sliderElement);

      this.blazeInstance = new window.BlazeSlider(sliderElement, this.sliderConfig);

      this.dispatchEvent(new CustomEvent('slider-initialized', { 
        detail: this.blazeInstance 
      }));

      this.setupPagination();
      this.setupNavigation();
      this.setupResizeListener();
      
      setTimeout(() => {
        this.applyResponsiveConfig(sliderElement);
      }, 100);
    }
  }

  applyResponsiveConfig(sliderElement) {
    const width = window.innerWidth;
    let slidesToShow = this.sliderConfig.all.slidesToShow || 4;
    let slideGap = this.sliderConfig.all.slideGap || '1rem';
    
    if (width <= 400) {
      slidesToShow = this.sliderConfig['(max-width: 400px)']?.slidesToShow ?? slidesToShow;
      slideGap = this.sliderConfig['(max-width: 400px)']?.slideGap ?? slideGap;
    } else if (width <= 768) {
      slidesToShow = this.sliderConfig['(max-width: 768px)']?.slidesToShow ?? slidesToShow;
      slideGap = this.sliderConfig['(max-width: 768px)']?.slideGap ?? slideGap;
    } else if (width <= 1024) {
      slidesToShow = this.sliderConfig['(max-width: 1024px)']?.slidesToShow ?? slidesToShow;
      slideGap = this.sliderConfig['(max-width: 1024px)']?.slideGap ?? slideGap;
    } else if (width <= 1200) {
      slidesToShow = this.sliderConfig['(max-width: 1200px)']?.slidesToShow ?? slidesToShow;
      slideGap = this.sliderConfig['(max-width: 1200px)']?.slideGap ?? slideGap;
    }
    
    const slideWidth = `calc((100% - (${slidesToShow} - 1) * ${slideGap}) / ${slidesToShow})`;
    
    sliderElement.style.setProperty('--slides-to-show', slidesToShow);
    sliderElement.style.setProperty('--slide-gap', slideGap);
    sliderElement.style.setProperty('--slide-width', slideWidth);
    
    const track = sliderElement.querySelector('.blaze-track');
    if (track) {
      track.style.setProperty('--slides-to-show', slidesToShow);
      track.style.setProperty('--slide-gap', slideGap);
      track.style.setProperty('--slide-width', slideWidth);
      track.style.gap = slideGap;
    }
    
    const items = sliderElement.querySelectorAll('.content-item');
    items.forEach(item => {
      item.style.setProperty('--slides-to-show', slidesToShow);
      item.style.setProperty('--slide-gap', slideGap);
      item.style.setProperty('--slide-width', slideWidth);
    });
  }

  setupResizeListener() {
    const handleResize = () => {
      const sliderElement = this.querySelector('.blaze-slider');
      if (sliderElement) {
        this.applyResponsiveConfig(sliderElement);
        if (this.blazeInstance?.recalculate) {
          this.blazeInstance.recalculate();
        }
      }
    };

    window.addEventListener('resize', handleResize);
    this.resizeHandler = handleResize;
  }

  setupPagination() {
    const paginationItems = this.querySelectorAll('.blaze-pagination-item');
    paginationItems.forEach((item, index) => {
      item.addEventListener('click', () => {
        this.goToSlide(index);
      });
    });
  }

  setupNavigation() {
    const prevButton = this.querySelector('.blaze-prev');
    const nextButton = this.querySelector('.blaze-next');

    if (prevButton) {
      prevButton.addEventListener('click', () => {
        this.blazeInstance?.prev();
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => {
        this.blazeInstance?.next();
      });
    }
  }

  goToSlide(index) {
    if (this.blazeInstance) {
      const currentIndex = this.blazeInstance.stateIndex;
      const diff = index - currentIndex;
      
      if (diff > 0) {
        this.blazeInstance.next(diff);
      } else if (diff < 0) {
        this.blazeInstance.prev(Math.abs(diff));
      }
    }
  }

  selectAnItem(handle) {
    const itemElement = this.querySelector(`[data-handle="${handle}"]`);
    if (itemElement && this.blazeInstance) {
      const index = Array.from(this.querySelectorAll('.content-item')).indexOf(itemElement);
      this.goToSlide(index);
    }
  }

  setupVideoControls() {
    const allContentItems = this.querySelectorAll('.content-item');
    
    allContentItems.forEach(item => {
      const videoUrl = item.dataset.videoUrl;
      
      if (!videoUrl) return;
      
      const imageContainer = item.querySelector('.content-item__image-container');
      
      if (!imageContainer) return;
      
      imageContainer.style.cursor = 'pointer';
      
      const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const videoType = item.dataset.videoType;
        const videoId = item.dataset.videoId;
        
        if (videoType === 'vimeo') {
          this.#playVimeoVideo(videoId);
        } else if (videoType === 'youtube') {
          this.#playYouTubeVideo(videoId);
        } else if (videoType === 'native') {
          this.#playNativeVideo(videoUrl);
        }
      };
      
      imageContainer.addEventListener('click', handleClick);
      imageContainer.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick(e);
        }
      });
    });
  }

  #playVimeoVideo(videoId) {
    if (!videoId) return;
    
    const overlay = this.#createFullscreenOverlay();
    const iframe = document.createElement('iframe');
    iframe.src = `https://player.vimeo.com/video/${videoId}?autoplay=1&autopause=0`;
    iframe.style.cssText = `
      width: 100%;
      height: 100%;
      border: none;
    `;
    iframe.frameBorder = '0';
    iframe.allow = 'autoplay; fullscreen; picture-in-picture';
    iframe.allowFullscreen = true;
    
    overlay.appendChild(iframe);
    this.#showOverlay(overlay);
  }

  #playYouTubeVideo(videoId) {
    if (!videoId) return;
    
    const overlay = this.#createFullscreenOverlay();
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    iframe.style.cssText = `
      width: 100%;
      height: 100%;
      border: none;
    `;
    iframe.frameBorder = '0';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
    iframe.allowFullscreen = true;
    
    overlay.appendChild(iframe);
    this.#showOverlay(overlay);
  }

  #playNativeVideo(videoUrl) {
    if (!videoUrl) return;
    
    const overlay = this.#createFullscreenOverlay();
    const video = document.createElement('video');
    video.src = videoUrl;
    video.controls = true;
    video.autoplay = true;
    video.style.cssText = `
      width: 100%;
      height: 100%;
      object-fit: contain;
    `;
    
    overlay.appendChild(video);
    this.#showOverlay(overlay);
  }

  #createFullscreenOverlay() {
    const overlay = document.createElement('div');
    overlay.className = 'content-carousel-video-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: #000;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    
    const closeButton = document.createElement('button');
    closeButton.innerHTML = '✕';
    closeButton.style.cssText = `
      position: absolute;
      top: 20px;
      right: 20px;
      background: rgba(255, 255, 255, 0.2);
      color: white;
      border: none;
      border-radius: 50%;
      width: 44px;
      height: 44px;
      font-size: 24px;
      cursor: pointer;
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background-color 0.3s ease;
    `;
    closeButton.setAttribute('aria-label', 'Close video');
    
    closeButton.addEventListener('mouseenter', () => {
      closeButton.style.background = 'rgba(255, 255, 255, 0.3)';
    });
    
    closeButton.addEventListener('mouseleave', () => {
      closeButton.style.background = 'rgba(255, 255, 255, 0.2)';
    });
    
    closeButton.addEventListener('click', () => {
      this.#closeOverlay(overlay);
    });
    
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        this.#closeOverlay(overlay);
      }
    });
    
    overlay.appendChild(closeButton);
    
    return overlay;
  }

  #showOverlay(overlay) {
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        this.#closeOverlay(overlay);
        document.removeEventListener('keydown', handleEscape);
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    overlay.dataset.escapeHandler = 'attached';
  }

  #closeOverlay(overlay) {
    if (!overlay || !overlay.parentNode) return;
    
    document.body.removeChild(overlay);
    document.body.style.overflow = '';
  }

  disconnectedCallback() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }
}

customElements.define('content-carousel', ContentCarousel);